import React, { useEffect, useState, RefObject, Dispatch, SetStateAction } from "react";
import VideoDisplay from "./VideoDisplay";
import VideoControls from "./VideoControls";
import styles from "./VideoPlayer.module.css";
import { Caption } from "../../types";
import { MediaItem } from "../../types";
import { calculatePlayerDuration, Project } from "../../services/durationService";
import CropOverlay from "../VideoEditor/CropOverlay";
import MediaBoundingBox from "../VideoEditor/MediaBoundingBox";
import { cleanupVideoElement } from "../../utils/videoCleanup"; // ✅ Import cleanup utility

interface VideoPlayerProps {
  video?: string;
  captions: Caption[];
  videoRef: RefObject<HTMLVideoElement>;
  onTimeUpdate: (time: number) => void;
  onDurationChange: (duration: number) => void;
  mediaItems: MediaItem[];
  mediaCurrentTime: number;             
  setMediaCurrentTime: Dispatch<SetStateAction<number>>;
  onVideoPlay?: () => void; // NEW: callback for play events on the media video
  onVideoPause?: () => void;
  project?: Project | null; // NEW: project data for audio duration
  onTimelineLoadingChange?: (isLoading: boolean) => void; // NEW: callback for loading state
  onCaptionDrag?: (captionId: string, newYPosition: number, newXPosition?: number) => void; // NEW: callback for caption dragging
  onCaptionClick?: (captionId: string) => void; // NEW: callback for caption click to open editor
  // Aspect ratio conversion props
  isConversionMode?: boolean;
  targetAspectRatio?: string | null;
  selectedMediaItem?: MediaItem | null;
  sourceAspectRatio?: string;
  onUpdateMediaPosition?: (itemId: string, deltaXPercent: number, deltaYPercent: number) => void;
  onUpdateMediaScale?: (itemId: string, scaleDelta: number) => void;
  onResetMediaTransform?: (itemId: string) => void; // NEW: reset position and scale
  aspectRatio?: string; // NEW: Output aspect ratio (16:9, 9:16, 1:1)
  shouldPlayVideoAudio?: boolean; // Video-only mode: true = play video audio, false = mute videos (for audio+media mode)
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  video,
  captions,
  videoRef,
  onTimeUpdate,
  onDurationChange,
  mediaItems,
  mediaCurrentTime,
  setMediaCurrentTime,
  onVideoPlay, // NEW
  onVideoPause,
  project, // NEW
  onTimelineLoadingChange, // NEW
  onCaptionDrag, // NEW
  onCaptionClick, // NEW: caption click callback
  isConversionMode, // NEW: Conversion props
  targetAspectRatio,
  selectedMediaItem,
  sourceAspectRatio,
  onUpdateMediaPosition,
  onUpdateMediaScale,
  onResetMediaTransform, // NEW
  aspectRatio, // NEW
  shouldPlayVideoAudio = false // NEW: default to false (muted videos)
}) => {
  // 🔍 DIAGNOSTIC: Log component lifecycle
  console.log('🎬 VideoPlayer RENDER - video prop:', video?.substring(0, 50) + '...');
  
  // State for video mode
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [calculatedDuration, setCalculatedDuration] = useState(0); // NEW: for audio/media duration
  const [isTimelineLoading, setIsTimelineLoading] = useState(true); // NEW: timeline loading state
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isVerticalVideo, setIsVerticalVideo] = useState(false);
  const [canvasDimensions, setCanvasDimensions] = useState({ width: 0, height: 0 }); // NEW: for crop overlay

  const vertFontRatio = 30;

  // 🔍 DIAGNOSTIC: Track component mount/unmount
  useEffect(() => {
    console.log('✅ VideoPlayer MOUNTED with video:', video?.substring(0, 50));
    return () => {
      console.log('❌ VideoPlayer UNMOUNTING with video:', video?.substring(0, 50));
    };
  }, []);

  // Setup video mode if video is provided.
  useEffect(() => {
    if (video) {
      const videoElement = videoRef.current;
      if (!videoElement) return;
      const handleLoadedMetadata = () => {
        const { videoWidth, videoHeight } = videoElement;
        const aspectRatio = videoWidth / videoHeight;
        setIsVerticalVideo(Math.abs(aspectRatio - (9 / 16)) < 0.01);
      };
      const handleTimeUpdate = () => {
        const time = videoElement.currentTime;
        console.log("Video time update:", time);
        setCurrentTime(time);
        onTimeUpdate(time);
      };
      const handleDurationChange = () => {
        setDuration(videoElement.duration);
        onDurationChange(videoElement.duration);
      };
      const handlePlay = () => setIsPlaying(true);
      const handlePause = () => setIsPlaying(false);
      videoElement.addEventListener("loadedmetadata", handleLoadedMetadata);
      videoElement.addEventListener("timeupdate", handleTimeUpdate);
      videoElement.addEventListener("durationchange", handleDurationChange);
      videoElement.addEventListener("play", handlePlay);
      videoElement.addEventListener("pause", handlePause);
      if (videoElement.duration) {
        handleDurationChange();
      }
      return () => {
        videoElement.removeEventListener("loadedmetadata", handleLoadedMetadata);
        videoElement.removeEventListener("timeupdate", handleTimeUpdate);
        videoElement.removeEventListener("durationchange", handleDurationChange);
        videoElement.removeEventListener("play", handlePlay);
        videoElement.removeEventListener("pause", handlePause);
        
        // ❌ DON'T cleanup here - this runs when the video URL changes
        // which prevents the new video from loading properly
        // Cleanup should only happen on component unmount
      };
    }
  }, [video, videoRef, onTimeUpdate, onDurationChange]);

  // ✅ Cleanup video element ONLY on component unmount (not on video URL change)
  // useEffect(() => {
  //   return () => {
  //     if (videoRef.current) {
  //       console.log('🧹 VideoPlayer unmounting - cleaning up video element');
  //       cleanupVideoElement(videoRef.current);
  //     }
  //   };
  // }, []); // Empty deps = runs only on unmount

  // Calculate duration using the duration service
  useEffect(() => {
    const calculateDuration = async () => {
      setIsTimelineLoading(true);
      
      const result = await calculatePlayerDuration(project, mediaItems, video ? duration : undefined);
      setCalculatedDuration(result.duration);
      
      // Notify parent of the calculated duration
      if (result.duration > 0 && onDurationChange) {
        onDurationChange(result.duration);
      }
      
      // Timeline is no longer loading when:
      // 1. Audio has loaded successfully, OR
      // 2. We're using media items duration (fallback), OR  
      // 3. We have a video file
      const hasValidDuration = result.duration > 0;
      const audioLoadedOrFallback = result.isAudioLoaded || mediaItems.length > 0 || video;
      
      if (hasValidDuration && audioLoadedOrFallback) {
        setIsTimelineLoading(false);
      }
      
      // Notify parent component of loading state change
      if (onTimelineLoadingChange) {
        onTimelineLoadingChange(!hasValidDuration || !audioLoadedOrFallback);
      }
    };

    calculateDuration();
  }, [project, mediaItems, video, duration, onTimelineLoadingChange, onDurationChange]);

  // For media mode: if no video is provided, and if active media is an image,
  // use an interval to simulate playback.
  useEffect(() => {
    if (!video && mediaItems.length > 0 && isPlaying) {
      const activeMedia = mediaItems.find(
        (item) => mediaCurrentTime >= item.start && mediaCurrentTime < item.end
      );
      // for image, use interval for 5sec
      if (!activeMedia || activeMedia.type !== "video") {
        const intervalId = setInterval(() => {
          setMediaCurrentTime((prev) => {
            const lastEnd = mediaItems[mediaItems.length - 1].end;
            if (prev < lastEnd) {
              const newTime = prev + 0.5;
              onTimeUpdate(newTime);
              return newTime;
            } else {
              setIsPlaying(false);
              clearInterval(intervalId);
              return prev;
            }
          });
        }, 500);
        return () => clearInterval(intervalId);
      }
    }
  }, [video, mediaItems, isPlaying, mediaCurrentTime, onTimeUpdate, setMediaCurrentTime]);

  // Determine active captions based on the unified time.
  const timeForCaptions = video ? currentTime : mediaCurrentTime;
  const activeCaptions: Caption[] = captions.filter(
    (caption) => timeForCaptions >= caption.start && timeForCaptions <= caption.end
  );

  // NEW: Filter out hidden media items for display
  const visibleMediaItems = mediaItems.filter(item => !item.hidden);

  return (
    <div style={{ position: 'relative' }}>
      <VideoDisplay
        video={video}
        mediaItems={visibleMediaItems} // NEW: Use filtered media items
        videoRef={videoRef}
        isVerticalVideo={isVerticalVideo}
        isPlaying={isPlaying}
        currentTime={currentTime}
        mediaCurrentTime={mediaCurrentTime}  // Use parent's value
        vertFontRatio={vertFontRatio}
        activeCaptions={activeCaptions}
        onMediaTimeUpdate={(time: number) => {
          console.log("----Media time update from VideoDisplay:", time);
          setMediaCurrentTime(time);  //The videoDisplay should not ajust anything time from outside. Its jusr play and stop
          onTimeUpdate(time);
        }}
        onCaptionDrag={onCaptionDrag} // NEW: Pass caption drag callback
        onCaptionClick={onCaptionClick} // NEW: Pass caption click callback
        onCanvasDimensionsChange={setCanvasDimensions} // NEW: Track canvas dimensions
        isConversionMode={isConversionMode}
        onUpdateMediaPosition={onUpdateMediaPosition}
        onUpdateMediaScale={onUpdateMediaScale}
        onResetMediaTransform={onResetMediaTransform} // NEW: Pass reset handler
        aspectRatio={aspectRatio} // Output aspect ratio
        targetAspectRatio={targetAspectRatio} // Target when in conversion mode
        shouldPlayVideoAudio={shouldPlayVideoAudio} // FIXED: Pass through the audio flag!
      />

      {/* NEW: Crop Overlay for aspect ratio conversion (fixed 9:16 frame, no dots) */}
      {isConversionMode && targetAspectRatio && sourceAspectRatio && (
        <CropOverlay
          isActive={isConversionMode}
          sourceAspectRatio={sourceAspectRatio}
          targetAspectRatio={targetAspectRatio}
          canvasDimensions={canvasDimensions}
          selectedMediaItem={selectedMediaItem}
        />
      )}

      {/* NEW: Media Bounding Box (follows media, has corner dots for resizing) */}
      {isConversionMode && selectedMediaItem && sourceAspectRatio && (
        <MediaBoundingBox
          isActive={isConversionMode}
          selectedMediaItem={selectedMediaItem}
          canvasDimensions={canvasDimensions}
          sourceAspectRatio={sourceAspectRatio}
          onUpdateScale={onUpdateMediaScale}
        />
      )}

      <VideoControls
        video={video}
        videoRef={videoRef}
        currentTime={video ? currentTime : mediaCurrentTime}
        duration={video ? duration : calculatedDuration}
        isPlaying={isPlaying}
        volume={volume}
        isMuted={isMuted}
        setIsPlaying={setIsPlaying}
        setVolume={setVolume}
        setIsMuted={setIsMuted}
        setCurrentTime={(time: number) => {
          if (video && videoRef.current) {
            videoRef.current.currentTime = time;
            setCurrentTime(time);
          } else {
            setMediaCurrentTime(time);
            onTimeUpdate(time);
          }
        }}
        onVideoPlay={onVideoPlay} // NEW: Pass the video play callback to VideoControls
        onVideoPause={onVideoPause} // NEW: Pass the video play callback to VideoControls
      />
    </div>
  );
};

export default VideoPlayer;
